import { useState, useMemo, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { filterMessagesByTheme } from '../../../lib/utils/sentimentThemeMessages'
import { formatLocalDateKey } from '../../../utils/dateRangeUtils'
import './SentimentThemeDrawer.css'

const toDateStr = (d) => formatLocalDateKey(d)

const PLATFORM_LABELS = {
  instagram: 'Instagram',
  facebook: 'Facebook',
  linkedin: 'LinkedIn',
  twitter: 'X',
  tiktok: 'TikTok',
}

const PAGE_SIZE = 20

const SentimentThemeDrawer = ({
  isOpen,
  onClose,
  selectedTheme,
  selectedSentiment,
  messages = [],
  dateRange,
  platformFilter,
  dateRangeLabel = 'Last 30 days',
}) => {
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const [sortOption, setSortOption] = useState('recent')
  const [page, setPage] = useState(0)

  const sortOptions = useMemo(() => {
    const opts = [{ value: 'recent', label: 'Most recent' }]
    if (selectedSentiment === 'positive') {
      opts.push({ value: 'score_desc', label: 'Highest positivity' })
    } else if (selectedSentiment === 'negative') {
      opts.push({ value: 'score_asc', label: 'Highest negativity' })
    }
    return opts
  }, [selectedSentiment])

  const effectiveSortOption = useMemo(() => {
    const validValues = sortOptions.map((o) => o.value)
    return validValues.includes(sortOption) ? sortOption : 'recent'
  }, [sortOption, sortOptions])

  useEffect(() => {
    const validValues = sortOptions.map((o) => o.value)
    if (!validValues.includes(sortOption)) {
      setSortOption('recent')
    }
  }, [selectedSentiment, sortOptions, sortOption])

  const platformLabel = useMemo(() => {
    if (!platformFilter || platformFilter === 'all') return 'All'
    if (Array.isArray(platformFilter) && platformFilter.length === 0) return 'All'
    if (Array.isArray(platformFilter)) {
      return platformFilter.map((p) => PLATFORM_LABELS[p] || p).join(', ')
    }
    return PLATFORM_LABELS[platformFilter] || platformFilter
  }, [platformFilter])

  const filteredMessages = useMemo(() => {
    if (!selectedTheme || !selectedSentiment) return []
    const range = dateRange
      ? { start: toDateStr(dateRange.start), end: toDateStr(dateRange.end) }
      : {}
    const platform = Array.isArray(platformFilter) && platformFilter.length > 0 ? platformFilter : platformFilter || 'all'
    return filterMessagesByTheme(messages, selectedSentiment, selectedTheme, {
      dateRange: range,
      platformFilter: platform,
      searchQuery,
      sortOption: effectiveSortOption,
    })
  }, [messages, selectedTheme, selectedSentiment, dateRange, platformFilter, searchQuery, effectiveSortOption])

  const paginatedMessages = useMemo(() => {
    const start = page * PAGE_SIZE
    return filteredMessages.slice(start, start + PAGE_SIZE)
  }, [filteredMessages, page])

  const totalPages = Math.ceil(filteredMessages.length / PAGE_SIZE)
  const hasMore = page < totalPages - 1

  const handleViewInInbox = () => {
    const params = new URLSearchParams()
    params.set('sentiment', selectedSentiment)
    params.set('theme', selectedTheme)
    params.set('platform', platformFilter === 'all' || !platformFilter ? 'all' : (Array.isArray(platformFilter) ? platformFilter.join(',') : platformFilter))
    if (dateRange?.start) params.set('from', formatLocalDateKey(dateRange.start))
    if (dateRange?.end) params.set('to', formatLocalDateKey(dateRange.end))
    navigate(`/ai-community-inbox?${params.toString()}`)
    onClose()
  }

  const formatTimestamp = (iso) => {
    if (!iso) return '—'
    const d = new Date(iso)
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
  }

  const formatScore = (score) => {
    if (score == null) return '—'
    const pct = Math.round((score + 1) * 50)
    return `${pct}%`
  }

  if (!isOpen) return null

  return (
    <div className="std-drawer-overlay" onClick={onClose} role="presentation">
      <div className="std-drawer-panel" onClick={(e) => e.stopPropagation()} role="dialog" aria-labelledby="std-drawer-title">
        <div className="std-drawer-header">
          <div>
            <h2 id="std-drawer-title" className="std-drawer-title">
              Messages
            </h2>
            <p className="std-drawer-subtitle">
              Filtered: {selectedSentiment === 'positive' ? 'Positive' : 'Negative'} · Theme: &quot;{selectedTheme}&quot;
            </p>
            <p className="std-drawer-filters">
              {dateRangeLabel} · Platform: {platformLabel}
            </p>
          </div>
          <button type="button" className="std-drawer-close" onClick={onClose} aria-label="Close">
            <span className="std-close-icon" />
          </button>
        </div>

        <div className="std-drawer-controls">
          <input
            type="search"
            className="std-drawer-search"
            placeholder="Search within results..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value)
              setPage(0)
            }}
            aria-label="Search within results"
          />
          <select
            className="std-drawer-sort"
            value={effectiveSortOption}
            onChange={(e) => {
              setSortOption(e.target.value)
              setPage(0)
            }}
            aria-label="Sort by"
          >
            {sortOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <button type="button" className="std-drawer-clear" onClick={onClose}>
            Clear
          </button>
        </div>

        <div className="std-drawer-body">
          {filteredMessages.length === 0 ? (
            <div className="std-drawer-empty">
              No messages match the selected filters.
            </div>
          ) : (
            <ul className="std-drawer-list">
              {paginatedMessages.map((msg) => (
                <li key={msg.id} className="std-drawer-list-item">
                  <div className="std-drawer-item-row">
                    <span className="std-drawer-item-time">{formatTimestamp(msg.createdAt)}</span>
                    <span className={`std-drawer-item-platform std-drawer-item-platform--${msg.platform}`}>
                      {PLATFORM_LABELS[msg.platform] || msg.platform}
                    </span>
                  </div>
                  <div className="std-drawer-item-meta">
                    <span className={`std-drawer-item-sentiment std-drawer-item-sentiment--${msg.sentimentLabel}`}>
                      {msg.sentimentLabel}
                    </span>
                    {msg.sentimentScore != null && (
                      <span className="std-drawer-item-score">{formatScore(msg.sentimentScore)}</span>
                    )}
                    {msg.handoff && <span className="std-drawer-item-handoff">Handoff</span>}
                  </div>
                  <p className="std-drawer-item-preview">
                    {(msg.text || '').slice(0, 120)}
                    {(msg.text || '').length > 120 ? '…' : ''}
                  </p>
                  <a
                    href={`/ai-community-inbox?thread=${msg.threadId}`}
                    className="std-drawer-item-link"
                    onClick={(e) => {
                      e.preventDefault()
                      navigate(`/ai-community-inbox?thread=${msg.threadId}`)
                      onClose()
                    }}
                  >
                    Open in Inbox
                  </a>
                </li>
              ))}
            </ul>
          )}
          {hasMore && filteredMessages.length > 0 && (
            <button
              type="button"
              className="std-drawer-load-more"
              onClick={() => setPage((p) => p + 1)}
            >
              Load more
            </button>
          )}
        </div>

        <div className="std-drawer-footer">
          <button type="button" className="std-drawer-primary-btn" onClick={handleViewInInbox}>
            View in Inbox
          </button>
        </div>
      </div>
    </div>
  )
}

export default SentimentThemeDrawer
